import { useCallback, useEffect } from 'react';
import { skipToken } from '@apollo/client';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import useUserLocation from 'global-state/userLocation/useUserLocation';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import useModularPdp from 'global-state/modularPdp/useModularPdp';
import useSetModularPdp from 'global-state/modularPdp/useSetModularPdp';
import { GET_MODULAR_SOFA_BUNDLE } from './queries';
import { getBundleProductsWithOrientation } from './utils';
import useProductCardOptions from '../useProductCardOptions';

const useModularPDPState = (id, slug, queryOptions = {}) => {
  const { skip = false, skipQuery = true } = queryOptions;

  const modularState = useModularPdp();
  const setModularPdp = useSetModularPdp();

  const userLocation = useUserLocation();
  const skipModularBundleQuery = skip || skipQuery;

  const {
    loading: modularBundleLoading,
    data: modularBundleData,
    error: modularBundleError,
  } = useSuspenseQuery(
    GET_MODULAR_SOFA_BUNDLE,
    isNewConsumerApp && skipModularBundleQuery
      ? skipToken
      : {
          variables: {
            slug,
            zipCode: userLocation?.zip,
            ...getVisitorLocationQueryVariables(userLocation?.stateShortName),
          },
          skip: skipModularBundleQuery,
        }
  );

  const { data: optionsData } = useProductCardOptions(
    modularBundleData?.modularBundle?.modular_parent?.id,
    undefined,
    undefined,
    {
      skip,
    }
  );

  const toggleAddProductsSidebar = useCallback(
    isOpen => {
      setModularPdp(prevModularPdp => ({
        ...prevModularPdp,
        isModularAddProductSidebarOpen: isOpen,
      }));
    },
    [setModularPdp]
  );

  const updateModularOrderStatus = useCallback(
    updatedOrder => {
      if (!updatedOrder) {
        return;
      }

      setModularPdp(prevModularPdp => ({
        ...prevModularPdp,
        modularBundleOrderStatus: updatedOrder,
      }));
    },
    [setModularPdp]
  );

  const updateModularOrderTotal = useCallback(
    updatedPrice => {
      setModularPdp(prevModularPdp => ({
        ...prevModularPdp,
        modularTotalPrice: updatedPrice,
      }));
    },
    [setModularPdp]
  );

  const updateCurrentGalleryProduct = useCallback(
    updatedProduct => {
      setModularPdp(prevModularPdp => {
        if (
          !updatedProduct ||
          prevModularPdp?.currentModularGalleryProduct?.id ===
            updatedProduct?.id
        ) {
          return prevModularPdp;
        }

        return {
          ...prevModularPdp,
          currentModularGalleryProduct: updatedProduct,
        };
      });
    },
    [setModularPdp]
  );

  const setModularSectionalProduct = useCallback(
    sectionalProduct => {
      setModularPdp(prevModularPdp => ({
        ...prevModularPdp,
        modularSectionalProduct: sectionalProduct,
      }));

      updateCurrentGalleryProduct(sectionalProduct);
    },
    [setModularPdp, updateCurrentGalleryProduct]
  );

  useEffect(() => {
    if (skip) {
      return;
    }

    if (
      modularBundleData?.modularBundle &&
      optionsData?.options &&
      modularState?.modularSlug !==
        modularBundleData?.modularBundle?.modular_parent?.slug
    ) {
      const {
        modularBundle: { modular_parent, products, preConfiguredProducts },
      } = modularBundleData;

      const collectionTitle = modular_parent?.name
        ? modular_parent.name.split(' ')[0]
        : '';

      const processedBundleProducts = getBundleProductsWithOrientation(
        products
      );

      setModularPdp(prevModularPdp => ({
        ...prevModularPdp,
        modularParentSlug: modular_parent.slug,
        collectionTitle,
        preConfiguredProducts,
        bundleProducts: processedBundleProducts,
      }));
    }
  }, [
    modularBundleData,
    modularState?.modularSlug,
    optionsData.options,
    setModularPdp,
    skip,
  ]);

  if (skip) {
    return {};
  }

  return {
    loading: modularBundleLoading,
    error: modularBundleError,
    data: {
      modularState,
    },
    actions: {
      toggleAddProductsSidebar,
      updateModularOrderStatus,
      updateModularOrderTotal,
      setModularSectionalProduct,
      updateCurrentGalleryProduct,
    },
  };
};

export default useModularPDPState;
